import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth, API } from "@/context/AuthContext";
import { DASHBOARD, FORM } from "@/constants/testIds";
import { urgencyOf, formatDate } from "@/lib/dateUtils";
import CountdownBadge from "@/components/CountdownBadge";
import Modal from "@/components/Modal";
import OrderForm from "@/components/OrderForm";
import OrderDetailsModal from "@/components/OrderDetailsModal";
import OrderSuccessPanel from "@/components/OrderSuccessPanel";

const OPEN_STATUSES = new Set(["design", "waiting_approval", "production", "ready"]);

const STATUS_TONE = {
  design: "bg-slate-100 text-slate-800 border-slate-300",
  waiting_approval: "bg-amber-100 text-amber-900 border-amber-300",
  production: "bg-blue-100 text-blue-900 border-blue-300",
  ready: "bg-emerald-100 text-emerald-900 border-emerald-300",
  delivered: "bg-neutral-200 text-neutral-600 border-neutral-300",
};

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// GP 1.0 · Sprint C — GP-R006
// El flujo oficial es Estimate → Invoice → Work Order → Producción → Entrega
// → Cierre. Las órdenes ya no se crean manualmente desde este panel, por lo
// que el botón "Nueva Orden" queda oculto. El endpoint, el modal, el estado
// `editing` y toda la lógica de creación/edición se conservan intactos y
// pueden reactivarse volviendo esta bandera a `true`.
const SHOW_NEW_ORDER_BUTTON = false;

export default function OrdersPanel({ onChanged, compact = false, onOpenPricingRule }) {
  const { t, lang } = useI18n();
  const { isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  // Details modal
  const [detailsOrder, setDetailsOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Post-save share panel (Customer Copy: Print / PDF / Email / SMS / WhatsApp)
  const [successOrder, setSuccessOrder] = useState(null);

  // GP 1.0 · Sprint 1 — Tracking view enrichment.
  // Employees: used both by OrderDetailsModal AND to resolve assigned_employee_id
  // per-item into human-readable names in the row.
  // Tasks: used to compute per-order task counters (X/Y done) directly in the row.
  const [employees, setEmployees] = useState([]);
  const [allTasks, setAllTasks] = useState([]);

  // Section: "active" (default) | "history" (Admin only)
  const [section, setSection] = useState("active");

  // Active orders search (partial, real-time)
  const [activeQuery, setActiveQuery] = useState("");

  // History search
  const [historyQuery, setHistoryQuery] = useState("");
  const [historyResults, setHistoryResults] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/orders`);
      setOrders(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Load employees (needed for tracking columns + task creation buttons).
  // Note: /api/employees is available to both admins and workers.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/employees`)
      .then(({ data }) => {
        if (!cancelled) setEmployees(Array.isArray(data) ? data : []);
      })
      .catch(() => !cancelled && setEmployees([]));
    return () => {
      cancelled = true;
    };
  }, []);

  // Load tasks (used to render "Tareas X/Y" counter per order). Skip in compact
  // mode to avoid extra work on the main dashboard where the counter isn't shown.
  //
  // Sprint 4 · Group B — Extracted into a stable callback so the OrderDetails
  // modal can trigger an immediate refetch after creating/updating/closing a
  // task (Issue #12). Without this, the row counter drifts out of sync until
  // the orders list itself changes.
  const refetchTasks = useCallback(async () => {
    if (compact) return;
    try {
      const { data } = await axios.get(`${API}/tasks`);
      setAllTasks(Array.isArray(data) ? data : []);
    } catch {
      setAllTasks([]);
    }
  }, [compact]);
  useEffect(() => {
    if (compact) return;
    let cancelled = false;
    axios
      .get(`${API}/tasks`)
      .then(({ data }) => {
        if (!cancelled) setAllTasks(Array.isArray(data) ? data : []);
      })
      .catch(() => !cancelled && setAllTasks([]));
    return () => {
      cancelled = true;
    };
  }, [compact, orders.length]);

  // Debounced history fetch
  useEffect(() => {
    if (compact || section !== "history" || !isAdmin) return;
    const handler = setTimeout(async () => {
      setHistoryLoading(true);
      try {
        const params = historyQuery.trim() ? { q: historyQuery.trim() } : {};
        const { data } = await axios.get(`${API}/orders/history`, { params });
        setHistoryResults(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error");
      } finally {
        setHistoryLoading(false);
      }
    }, 250);
    return () => clearTimeout(handler);
  }, [historyQuery, section, compact, isAdmin]);

  // Open orders first, always sorted by nearest delivery_date.
  const sorted = useMemo(() => {
    return [...orders].sort((a, b) => {
      const aOpen = OPEN_STATUSES.has(a.status);
      const bOpen = OPEN_STATUSES.has(b.status);
      if (aOpen !== bOpen) return aOpen ? -1 : 1;
      return (a.delivery_date || "9999-12-31").localeCompare(b.delivery_date || "9999-12-31");
    });
  }, [orders]);

  // Feb 2026 · bugfix — En modo full, la sección "Active" debe excluir
  // órdenes en estado `delivered`: cuando una WO se marca como entregada
  // debe salir de la lista de pendientes. `renderHistory()` es la vía
  // oficial para verlas después. Compact ya filtraba correctamente.
  const visible = compact
    ? sorted.filter((o) => OPEN_STATUSES.has(o.status)).slice(0, 8)
    : sorted.filter((o) => OPEN_STATUSES.has(o.status));

  // Apply real-time partial search filter on active orders (not for compact view).
  const filteredVisible = useMemo(() => {
    if (compact) return visible;
    const q = activeQuery.trim().toLowerCase();
    if (!q) return visible;
    return visible.filter((o) => {
      const fields = [
        o.company_name,
        o.customer_name,
        o.contact_person,
        o.invoice_number,
        o.order_number,
        o.wo_number,
        o.source_invoice_number,
        o.customer_phone,
        o.product,
      ];
      return fields.some((f) => String(f || "").toLowerCase().includes(q));
    });
  }, [visible, activeQuery, compact]);

  // GP 1.0 · Sprint 1 — Tracking helpers.
  // employeesById: fast UUID → employee record lookup for showing assignee names.
  // tasksByOrder: bucket tasks by order_id to render "X/Y done" counters.
  const employeesById = useMemo(() => {
    const m = {};
    for (const e of employees) m[e.id] = e;
    return m;
  }, [employees]);
  const tasksByOrder = useMemo(() => {
    const m = {};
    for (const tk of allTasks) {
      if (!tk?.order_id) continue;
      (m[tk.order_id] ||= []).push(tk);
    }
    return m;
  }, [allTasks]);

  const submit = async (payload) => {
    try {
      if (editing?.id) {
        await axios.put(`${API}/orders/${editing.id}`, payload);
        toast.success(t("save"));
        setOpen(false);
        setEditing(null);
        load();
        onChanged && onChanged();
      } else {
        const { data: created } = await axios.post(`${API}/orders`, payload);
        toast.success(t("save"));
        // Close the edit form and open the customer-copy share panel so the
        // salesperson can immediately hand the order to the customer.
        setOpen(false);
        setEditing(null);
        setSuccessOrder(created);
        load();
        onChanged && onChanged();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await axios.delete(`${API}/orders/${id}`);
      toast.success(t("delete"));
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const downloadPdf = async (order) => {
    try {
      // GP 1.0 · Sprint 1 — Work Orders use a dedicated PDF endpoint (no financials).
      const isWO = Boolean(order.wo_number);
      const url = isWO
        ? `${API}/commercial/work-orders/${order.id}/pdf`
        : `${API}/orders/${order.id}/pdf`;
      const res = await axios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `RAWENS_${order.wo_number || order.invoice_number || order.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF error");
    }
  };

  const openDetails = (order) => {
    setDetailsOrder(order);
    setDetailsOpen(true);
  };

  // ----- Render: History section (Admin only, non-compact) -----
  const renderHistory = () => (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <input
          data-testid={DASHBOARD.orderHistorySearch}
          type="text"
          value={historyQuery}
          onChange={(e) => setHistoryQuery(e.target.value)}
          placeholder={t("search_placeholder")}
          className="flex-1 border border-[var(--border)] bg-white px-4 py-2.5 font-display focus:outline-none focus:border-black"
        />
        {historyQuery && (
          <button
            onClick={() => setHistoryQuery("")}
            className="border border-[var(--border)] px-4 py-2.5 text-sm hover:bg-[var(--surface-alt)]"
          >
            ✕
          </button>
        )}
      </div>

      {historyLoading ? (
        <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
          LOADING…
        </div>
      ) : historyResults.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] bg-white py-16 text-center text-[var(--text-secondary)]">
          {t("no_history_results")}
        </div>
      ) : (
        <div
          data-testid={DASHBOARD.orderHistoryList}
          className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]"
        >
          {historyResults.map((o) => (
            <div
              key={o.id}
              data-testid={DASHBOARD.orderHistoryRow(o.id)}
              className="grid grid-cols-12 gap-4 items-center px-5 py-4 hover:bg-[var(--bg)]"
            >
              <div className="col-span-12 md:col-span-2">
                <div className="label-overline md:hidden mb-0.5">{t("invoice_number")}</div>
                <div className="font-mono-tech text-base font-bold tracking-tight">
                  #{o.invoice_number || "—"}
                </div>
                <div className="font-mono-tech text-[11px] text-[var(--text-secondary)] mt-0.5">
                  {formatDate(o.delivery_date, lang)}
                </div>
              </div>
              <div className="col-span-12 md:col-span-4">
                <div className="font-display text-base font-bold tracking-tight">
                  {o.company_name}
                </div>
                {o.customer_name && (
                  <div className="text-xs text-[var(--text-secondary)]">{o.customer_name}</div>
                )}
              </div>
              <div className="col-span-6 md:col-span-3">
                {o.contact_person && (
                  <div className="text-sm">{o.contact_person}</div>
                )}
                {o.customer_phone && (
                  <div className="font-mono-tech text-xs text-[var(--text-secondary)]">
                    ☎ {o.customer_phone}
                  </div>
                )}
              </div>
              <div className="col-span-6 md:col-span-3 flex md:justify-end gap-2">
                <button
                  data-testid={DASHBOARD.orderViewBtn(o.id)}
                  onClick={() => openDetails(o)}
                  className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
                >
                  {t("view_details")}
                </button>
                <button
                  data-testid={DASHBOARD.orderPdfBtn(o.id)}
                  onClick={() => downloadPdf(o)}
                  className="border border-black bg-black text-white px-3 py-1.5 text-sm font-display font-bold tracking-wide hover:bg-white hover:text-black"
                >
                  PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ----- Render: Active orders section -----
  const renderActive = () => (
    <>
      {!compact && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <input
            data-testid={DASHBOARD.ordersSearchInput}
            type="text"
            value={activeQuery}
            onChange={(e) => setActiveQuery(e.target.value)}
            placeholder={t("search_orders_placeholder")}
            className="flex-1 border border-[var(--border)] bg-white px-4 py-2.5 font-display focus:outline-none focus:border-black"
          />
          {activeQuery && (
            <button
              onClick={() => setActiveQuery("")}
              className="border border-[var(--border)] px-4 py-2.5 text-sm hover:bg-[var(--surface-alt)]"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {!loading && filteredVisible.length > 0 && !compact && (
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-2 label-overline border-b border-[var(--border)] bg-white">
          <div className="col-span-3">{t("delivery_date")} · {t("days_remaining_label")}</div>
          <div className="col-span-2">Doc # · Origen</div>
          <div className="col-span-3">{t("company_name")} · Contacto</div>
          <div className="col-span-2">Asignado · Progreso</div>
          <div className="col-span-2 text-right">Estado · Acciones</div>
        </div>
      )}
      {!loading && filteredVisible.length > 0 && compact && (
        <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-2 label-overline border-b border-[var(--border)] bg-white">
          <div className="col-span-3">{t("delivery_date")} · {t("days_remaining_label")}</div>
          <div className="col-span-2">{t("invoice_number")}</div>
          <div className="col-span-3">{t("company_name")} · {t("customer_phone")}</div>
          <div className="col-span-2 text-right">{t("pending_balance")}</div>
          <div className="col-span-2 text-right">·</div>
        </div>
      )}

      {loading ? (
        <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
          LOADING…
        </div>
      ) : filteredVisible.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] bg-white py-16 text-center text-[var(--text-secondary)]">
          {t("no_orders")}
        </div>
      ) : (
        <div className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]">
          {filteredVisible.map((o) => {
            const { bucket: _bucket } = urgencyOf(o.delivery_date);
            const isClosed = o.status === "delivered";
            // GP 1.0 · Sprint 1 — Tracking metadata (only computed in full view).
            const isWO = Boolean(o.wo_number);
            const docNumber = o.wo_number || o.invoice_number || "—";
            const originInvoice = o.source_invoice_number || "";
            const items = Array.isArray(o.items) ? o.items : [];
            const productLines = items.length
              ? items.map((it) => ({
                  qty: it?.quantity || 0,
                  desc: it?.description || "",
                }))
              : (o.product ? [{ qty: "", desc: o.product }] : []);
            const assignedEmployeeIds = Array.from(
              new Set(
                [
                  // Employees assigned at the item/product level (production ownership).
                  ...items.map((it) => it?.assigned_employee_id),
                  // Sprint 4 · Group B — Also include employees assigned via
                  // tasks so the row and the modal show the same set of people
                  // (Issue #7). Without this, delegated task workers were
                  // invisible in the Orders list.
                  ...(tasksByOrder[o.id] || []).map((tk) => tk?.employee_id),
                ].filter(Boolean)
              )
            );
            const assignedNames = assignedEmployeeIds
              .map((eid) => employeesById[eid]?.name || "—")
              .filter(Boolean);
            const orderTasks = tasksByOrder[o.id] || [];
            const tasksTotal = orderTasks.length;
            const tasksDone = orderTasks.filter((tk) => tk.status === "completed").length;
            // GP 1.0 · HOTFIX A — Progress is driven strictly by task completion
            // so the bar/percentage stay in lockstep with "Tareas X/Y".
            const progressPct = tasksTotal > 0
              ? Math.round((tasksDone / tasksTotal) * 100)
              : 0;
            const createdShort = (o.created_at || "").slice(0, 10);
            return (
              <div
                key={o.id}
                data-testid={DASHBOARD.orderCard(o.id)}
                className={`grid grid-cols-12 gap-4 items-start px-5 py-5 hover:bg-[var(--bg)] ${
                  isClosed ? "opacity-50" : ""
                }`}
              >
                {/* Delivery + countdown + created */}
                <div className="col-span-12 md:col-span-3">
                  <CountdownBadge
                    deliveryDate={o.delivery_date}
                    orderId={o.id}
                    size="lg"
                  />
                  <div className="font-mono-tech text-xs text-[var(--text-secondary)] mt-1.5">
                    {t("delivery_date")}: {formatDate(o.delivery_date, lang)}
                  </div>
                  {createdShort && !compact && (
                    <div
                      data-testid={`order-created-${o.id}`}
                      className="font-mono-tech text-[10px] text-[var(--text-secondary)] mt-0.5"
                    >
                      Creado: {createdShort}
                    </div>
                  )}
                </div>

                {/* Document number + origin + products (full) OR just invoice (compact) */}
                {compact ? (
                  <div className="col-span-6 md:col-span-2">
                    <div className="label-overline md:hidden mb-0.5">
                      {isWO ? "WO #" : t("invoice_number")}
                    </div>
                    <div
                      data-testid={`order-doc-number-${o.id}`}
                      className="font-mono-tech text-base font-bold tracking-tight"
                    >
                      {isWO ? docNumber : `#${docNumber}`}
                    </div>
                    {isWO && (
                      <span className="inline-block mt-1 border border-[var(--accent-cyan,#0E5FFF)] text-[var(--accent-cyan,#0E5FFF)] px-1.5 py-0.5 text-[9px] font-display font-bold tracking-wider">
                        WORK ORDER
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="col-span-12 md:col-span-2">
                    <div className="label-overline md:hidden mb-0.5">Documento</div>
                    <div
                      data-testid={`order-doc-number-${o.id}`}
                      className="font-mono-tech text-base font-bold tracking-tight"
                    >
                      {isWO ? docNumber : `#${docNumber}`}
                    </div>
                    {isWO && (
                      <span className="inline-block mt-1 border border-[var(--accent-cyan,#0E5FFF)] text-[var(--accent-cyan,#0E5FFF)] px-1.5 py-0.5 text-[9px] font-display font-bold tracking-wider">
                        WORK ORDER
                      </span>
                    )}
                    {originInvoice && (
                      <div
                        data-testid={`order-origin-${o.id}`}
                        className="font-mono-tech text-[10px] text-[var(--text-secondary)] mt-1"
                      >
                        Origen: {originInvoice}
                      </div>
                    )}
                  </div>
                )}

                {/* Company + Customer + Contact + Phone + Products */}
                <div className="col-span-12 md:col-span-3">
                  <div className="label-overline md:hidden mb-0.5">{t("company_name")}</div>
                  <div className="font-display text-lg font-bold tracking-tight">
                    {o.company_name || "—"}
                  </div>
                  {o.customer_name && (
                    <div className="text-xs text-[var(--text-secondary)]">
                      Cliente: {o.customer_name}
                    </div>
                  )}
                  {o.contact_person && (
                    <div className="text-xs text-[var(--text-secondary)]">
                      {o.contact_person}
                    </div>
                  )}
                  {o.customer_phone && (
                    <div className="font-mono-tech text-xs text-[var(--text-secondary)] mt-0.5">
                      ☎ {o.customer_phone}
                    </div>
                  )}
                  {!compact && productLines.length > 0 && (
                    <ul
                      data-testid={`order-products-${o.id}`}
                      className="mt-2 space-y-0.5 text-xs text-[var(--text-primary)]"
                    >
                      {productLines.slice(0, 3).map((p, idx) => (
                        <li key={idx} className="truncate">
                          <span className="font-mono-tech tabular-nums font-bold">
                            {p.qty !== "" ? `${p.qty}× ` : ""}
                          </span>
                          {p.desc || "—"}
                        </li>
                      ))}
                      {productLines.length > 3 && (
                        <li className="text-[10px] text-[var(--text-secondary)]">
                          +{productLines.length - 3} más…
                        </li>
                      )}
                    </ul>
                  )}
                </div>

                {/* Full view: assigned + tasks + progress. Compact: financial. */}
                {compact ? (
                  <div className="col-span-6 md:col-span-2 md:text-right">
                    <div className="label-overline md:hidden mb-0.5">{t("pending_balance")}</div>
                    <div
                      className={`font-mono-tech text-lg font-black tabular-nums ${
                        Number(o.pending_balance) > 0
                          ? "text-[var(--alert-overdue)]"
                          : "text-emerald-700"
                      }`}
                    >
                      {money(o.pending_balance)}
                    </div>
                    <div className="font-mono-tech text-[10px] text-[var(--text-secondary)] tabular-nums">
                      {money(o.deposit_received)} / {money(o.total_amount)}
                    </div>
                    <span
                      className={`mt-1 inline-block border px-2 py-0.5 text-[10px] font-display font-bold tracking-wide ${
                        STATUS_TONE[o.status] || "bg-slate-100 border-slate-300"
                      }`}
                    >
                      {t(`order_status_${o.status}`)}
                    </span>
                  </div>
                ) : (
                  <div className="col-span-12 md:col-span-2 space-y-1.5">
                    <div className="label-overline md:hidden mb-0.5">Asignado</div>
                    <div
                      data-testid={`order-assigned-${o.id}`}
                      className="text-xs"
                    >
                      {assignedNames.length === 0 ? (
                        <span className="italic text-[var(--text-secondary)]">
                          Sin asignar
                        </span>
                      ) : (
                        assignedNames.slice(0, 3).map((n, i) => (
                          <div key={i} className="font-display font-bold truncate">
                            👤 {n}
                          </div>
                        ))
                      )}
                      {assignedNames.length > 3 && (
                        <div className="text-[10px] text-[var(--text-secondary)]">
                          +{assignedNames.length - 3} más
                        </div>
                      )}
                    </div>
                    <div
                      data-testid={`order-tasks-count-${o.id}`}
                      className="font-mono-tech text-[11px] text-[var(--text-secondary)] tabular-nums"
                    >
                      Tareas: {tasksDone}/{tasksTotal}
                    </div>
                    <div
                      className="w-full h-2 bg-[var(--surface-alt,#f2f2f2)] border border-[var(--border)]"
                      title={`Progreso ${progressPct}%`}
                    >
                      <div
                        data-testid={`order-progress-bar-${o.id}`}
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <div className="font-mono-tech text-[10px] text-[var(--text-secondary)] tabular-nums">
                      {progressPct}% completado
                    </div>
                  </div>
                )}

                {/* Actions + status (full) */}
                <div className="col-span-12 md:col-span-2 flex md:flex-col justify-end gap-2 md:gap-1.5 flex-wrap md:items-end">
                  {!compact && (
                    <span
                      data-testid={`order-status-${o.id}`}
                      className={`inline-block border px-2 py-0.5 text-[10px] font-display font-bold tracking-wide whitespace-nowrap ${
                        STATUS_TONE[o.status] || "bg-slate-100 border-slate-300"
                      }`}
                    >
                      {t(`order_status_${o.status}`)}
                    </span>
                  )}
                  <div className="flex gap-1.5 flex-wrap md:justify-end">
                    <button
                      data-testid={DASHBOARD.orderViewBtn(o.id)}
                      onClick={() => openDetails(o)}
                      title={t("view_details")}
                      className="border border-[var(--border)] px-3 py-1.5 text-xs hover:bg-[var(--surface-alt)]"
                    >
                      {t("view_details")}
                    </button>
                    <button
                      data-testid={DASHBOARD.orderPdfBtn(o.id)}
                      onClick={() => downloadPdf(o)}
                      title={t("download_pdf")}
                      className="border border-black bg-black text-white px-3 py-1.5 text-xs font-display font-bold tracking-wide hover:bg-white hover:text-black"
                    >
                      PDF
                    </button>
                    {!isWO && (
                      <button
                        data-testid={FORM.orderEdit(o.id)}
                        onClick={() => {
                          setEditing(o);
                          setOpen(true);
                        }}
                        className="border border-[var(--border)] px-3 py-1.5 text-xs hover:bg-[var(--surface-alt)]"
                      >
                        {t("edit")}
                      </button>
                    )}
                    {!isWO && (
                      <button
                        data-testid={FORM.orderDelete(o.id)}
                        onClick={() => remove(o.id)}
                        className="border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-3 py-1.5 text-xs hover:bg-[var(--alert-overdue)] hover:text-white"
                      >
                        {t("delete")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div data-testid={DASHBOARD.ordersList} className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline">ORD / 01</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {t("orders_dashboard")}
          </h2>
          <p className="text-sm text-[var(--text-secondary)]">{t("sorted_by_delivery")}</p>
        </div>
        {SHOW_NEW_ORDER_BUTTON && (
          <button
            data-testid={DASHBOARD.newOrderBtn}
            onClick={() => {
              setEditing({});
              setOpen(true);
            }}
            className="bg-[var(--text-primary)] text-white px-5 py-2.5 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors"
          >
            + {t("new_order")}
          </button>
        )}
      </div>

      {/* Sub-tabs: Active / History (Admin, full view only) */}
      {!compact && isAdmin && (
        <div className="flex gap-1 border-b border-[var(--border)]">
          <button
            data-testid="tab-orders-active"
            onClick={() => setSection("active")}
            className={`px-4 py-2 font-display font-bold text-sm tracking-wide border-b-2 transition-colors ${
              section === "active"
                ? "border-black text-black"
                : "border-transparent text-[var(--text-secondary)] hover:text-black"
            }`}
          >
            {t("section_active")}
          </button>
          <button
            data-testid={DASHBOARD.tabOrderHistory}
            onClick={() => setSection("history")}
            className={`px-4 py-2 font-display font-bold text-sm tracking-wide border-b-2 transition-colors ${
              section === "history"
                ? "border-black text-black"
                : "border-transparent text-[var(--text-secondary)] hover:text-black"
            }`}
          >
            {t("section_history")}
          </button>
        </div>
      )}

      {section === "history" && !compact && isAdmin ? renderHistory() : renderActive()}

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        title={editing?.id ? t("edit_order") : t("new_order")}
        testId="order-modal"
        wide
      >
        <OrderForm
          initial={editing}
          onSubmit={submit}
          onCancel={() => {
            setOpen(false);
            setEditing(null);
          }}
          onPdfChanged={(info) => {
            // Optimistic merge so closing the modal (Cancel or Save) preserves PDF state
            setEditing((cur) =>
              cur && cur.id === info.orderId
                ? {
                    ...cur,
                    invoice_pdf_filename: info.invoice_pdf_filename,
                    invoice_pdf_size: info.invoice_pdf_size,
                    invoice_pdf_uploaded_at: info.invoice_pdf_uploaded_at,
                  }
                : cur
            );
            load();
            onChanged && onChanged();
          }}
        />
      </Modal>

      <OrderDetailsModal
        open={detailsOpen}
        order={detailsOrder}
        employees={employees}
        onTaskChanged={() => {
          // Sprint 4 · Group B — Refresh both orders (so item counts /
          // production_status changes propagate) AND tasks (so the row
          // counter stays in sync with reality — Issue #12).
          onChanged && onChanged();
          refetchTasks();
        }}
        onChanged={() => {
          // Feb 2026 — Sincroniza la lista principal cuando el estado de la
          // WO cambia dentro del modal (BUG 2). Sin este callback, el nuevo
          // estado no se reflejaba hasta recargar la página.
          load();
          onChanged && onChanged();
        }}
        onOpenPricingRule={onOpenPricingRule}
        onClose={() => {
          setDetailsOpen(false);
          setDetailsOrder(null);
          // Safety net: refresh tasks on close in case something changed
          // and the modal didn't fire onTaskChanged (e.g. quick edits).
          refetchTasks();
        }}
      />

      {successOrder && (
        <OrderSuccessPanel
          order={successOrder}
          onClose={() => setSuccessOrder(null)}
          onEditFull={() => {
            const o = successOrder;
            setSuccessOrder(null);
            setEditing(o);
            setOpen(true);
          }}
        />
      )}
    </div>
  );
}
